import React, { useState, useEffect } from 'react';
import { ArrowRight, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import { useCustomerSearch } from '../hooks/useCustomerSearch';

interface CustomerWithBalance extends Profile {
  balance?: number;
}

function TopUp() {
  const [amount, setAmount] = useState('0');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithBalance | null>(null);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const navigate = useNavigate();

  const { 
    searchResults, 
    searching, 
    searchCustomers, 
    setSearchResults 
  } = useCustomerSearch();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        setUserProfile(profile);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const handleNumberClick = (num: string) => {
    if (num === 'backspace') {
      setAmount(prev => prev.replace(/,/g, '').slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0');
    } else if (num === '.') {
      if (!amount.includes('.')) {
        setAmount(prev => prev + '.');
      }
    } else {
      setAmount(prev => 
        (prev === '0' ? num : prev + num)
      );
    }
  };

  const handleCustomerSearch = async (value: string) => {
    setSearchQuery(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }

    if (userProfile) {
      await searchCustomers(value, userProfile);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCustomer || loading) return;

    setLoading(true);
    setError(null);

    try {
      // Get vendor's smart key
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: vendorProfile, error: vendorError } = await supabase
        .from('profiles')
        .select('smart_key')
        .eq('id', user.id)
        .single();

      if (vendorError) throw vendorError;
      if (!vendorProfile) throw new Error('Vendor profile not found');

      // Parse amount and ensure it's positive
      const parsedAmount = Math.abs(parseFloat(amount.replace(/,/g, '')));
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error('Invalid amount');
      }

      // Create the transaction (always credit for TopUp)
      const { error: transactionError } = await supabase
        .from('transactions')
        .insert({
          vendor_smart_key: vendorProfile.smart_key,
          customer_smart_key: selectedCustomer.smart_key,
          amount: parsedAmount, // Positive amount for credit
          currency: selectedCustomer.currency,
          payment_type: 'credit',
          remarks: remarks.trim()
        });

      if (transactionError) {
        console.error('Transaction error:', transactionError);
        throw transactionError;
      }

      // Navigate back to home
      navigate('/home');
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 pt-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowRight size={24} className="text-gray-800" />
        </button>
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-semibold">إضافة رصيد</h1>
          <Tooltip text="إضافة رصيد للعميل. يمكنك تحديد المبلغ وإضافة ملاحظات للعملية." />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 rounded-xl text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Customer Search */}
      <div className="mb-8">
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="بحث عن عميل..."
            value={searchQuery}
            onChange={(e) => handleCustomerSearch(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
          />
          <div className="absolute left-3 flex items-center space-x-2">
            {searching ? (
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Search className="text-gray-400" size={20} />
            )}
          </div>
          <Tooltip 
            text="اكتب اسم العميل للبحث عنه. يجب أن يكون العميل مرتبطاً بحسابك مسبقاً."
            position="bottom"
          />
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && !selectedCustomer && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
            {searchResults.map((customer) => (
              <button
                key={customer.smart_key}
                onClick={() => {
                  setSelectedCustomer(customer);
                  setSearchQuery(customer.username);
                  setSearchResults([]);
                }}
                className="w-full px-4 py-3 text-right hover:bg-gray-50 flex justify-between items-center"
              >
                <span className="font-medium">{customer.username}</span>
                <span className="text-sm text-gray-500">
                  {customer.balance?.toFixed(2)} {customer.currency}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Amount */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-baseline">
          <span className="text-4xl font-semibold">{amount}</span>
        </div>
        <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-sm font-medium flex items-center space-x-1">
          <span>{selectedCustomer?.currency || 'ريال'}</span>
        </button>
      </div>

      {/* Selected Customer */}
      {selectedCustomer && (
        <div className="w-full bg-gray-50 rounded-xl p-4 space-y-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-medium">
                  {selectedCustomer.username.substring(0, 2).toUpperCase()}
                </span>
              </div>
              <div>
                <span className="font-medium">{selectedCustomer.username}</span>
                <p className="text-sm text-gray-500">
                  الرصيد: {selectedCustomer.balance?.toFixed(2)} {selectedCustomer.currency}
                </p>
              </div>
            </div>
            <button 
              onClick={() => {
                setSelectedCustomer(null);
                setSearchQuery('');
                setRemarks('');
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              تغيير
            </button>
          </div>
          
          {/* Remarks Field */}
          <div className="relative">
            <input
              type="text"
              placeholder="إضافة ملاحظات..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 bg-white"
            />
          </div>
        </div>
      )}

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, 'backspace'].map((num) => (
          <button
            key={num}
            onClick={() => handleNumberClick(num.toString())}
            className="h-14 rounded-2xl bg-gray-50 font-semibold text-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            {num === 'backspace' ? '⌫' : num}
          </button>
        ))}
      </div>

      {/* Top Up Button */}
      <button 
        onClick={handleSubmit}
        disabled={!selectedCustomer || loading || amount === '0'}
        className="w-full bg-green-500 text-white rounded-xl py-4 font-medium mt-8 hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'جاري المعالجة...' : 'إضافة رصيد'}
      </button>
    </div>
  );
}

export default TopUp;